import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  TrendingUp, Target, Users, IndianRupee, Calendar,
  CheckCircle, Clock, AlertTriangle, MapPin, Tag
} from 'lucide-react';

const parseValue = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const cleanStr = val.toString().replace(/₹|,/g, '').trim();
  if (cleanStr.includes('-')) {
    return parseFloat(cleanStr.split('-')[0].trim()) || 0;
  }
  return parseFloat(cleanStr) || 0;
};

const LeadsDashboard = ({ stats, dashboardData }) => {
  const [timeRange, setTimeRange] = useState('month');

  // Get leads data
  const leads = dashboardData?.leadData || [];

  // Process leads data
  const processLeadsData = () => {
    if (!leads.length) {
      return {
        totalLeads: 0,
        totalValue: 0,
        statusBreakdown: [],
        sourceBreakdown: [],
        recentLeads: []
      };
    }

    // Calculate total value
    const totalValue = leads.reduce((sum, lead) => sum + parseValue(lead.value || lead.budget || lead.amount), 0);

    // Status breakdown
    const statusMap = {};
    leads.forEach(lead => {
      const status = lead.status || lead.stage || 'Unknown';
      statusMap[status] = (statusMap[status] || 0) + 1;
    });

    const statusBreakdown = Object.entries(statusMap).map(([name, value]) => ({
      name,
      value
    }));

    // Source breakdown
    const sourceMap = {};
    leads.forEach(lead => {
      const source = lead.source || 'Unknown';
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const sourceBreakdown = Object.entries(sourceMap).map(([name, value]) => ({
      name,
      value
    }));

    // Recent leads (last 5)
    const recentLeads = [...leads]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);

    return {
      totalLeads: leads.length,
      totalValue,
      statusBreakdown,
      sourceBreakdown,
      recentLeads
    };
  };

  const { totalLeads, totalValue, statusBreakdown, sourceBreakdown, recentLeads } = processLeadsData();

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Leads */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalLeads}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <Target className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Total Value */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Lead Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(totalValue)}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <IndianRupee className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Average Value */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Lead Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {totalLeads > 0 ? formatCurrency(totalValue / totalLeads) : formatCurrency(0)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50 text-purple-600">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Active Leads */}
        <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Leads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalLeads}</p>
            </div>
            <div className="p-3 rounded-lg bg-orange-50 text-orange-600">
              <Clock className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Leads by Stage</h3>
          {statusBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No lead data available
            </div>
          )}
        </div>

        {/* Source Breakdown */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Leads by Source</h3>
          {sourceBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sourceBreakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No source data available
            </div>
          )}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Leads</h3>
        {recentLeads.length > 0 ? (
          <div className="space-y-3">
            {recentLeads.map((lead, index) => (
              <div
                key={lead._id || index}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-gray-900">{lead.name || lead.title || 'New Lead'}</h4>
                    {(lead.status || lead.stage) && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {lead.status || lead.stage}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    {(lead.email || lead.phone || lead.customer?.fullName) && (
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{lead.email || lead.phone || lead.customer?.fullName}</span>
                      </div>
                    )}
                    {(lead.value || lead.budget || lead.amount) ? (
                      <div className="flex items-center gap-1">
                        <IndianRupee className="w-4 h-4" />
                        <span>{formatCurrency(parseValue(lead.value || lead.budget || lead.amount))}</span>
                      </div>
                    ) : null}
                    {lead.source && (
                      <div className="flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        <span>{lead.source}</span>
                      </div>
                    )}
                    {lead.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{lead.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {new Date(lead.createdAt).toLocaleDateString('en-GB')}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No recent leads available
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsDashboard;
